import { useState } from 'react';
import DialogTemplate from '@/components/common/dialog-template/dialog-template';
import { Form, TextInput, useCreate, useNotify, useTranslate, useDataProvider, useInput } from 'react-admin';
import { useQueryClient } from '@tanstack/react-query';
import { PersonAddAltOutlined } from '@mui/icons-material';
import FormInputsSeparator from '@/components/common/form-inputs-separator/form-inputs-separator';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import ButtonsBlock from '@/components/common/buttons-block/buttons-block';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import ListSubheader from '@mui/material/ListSubheader';
import { useNavigate, useParams } from 'react-router';
import Joi from 'joi';
import { phoneExtension } from '@/utils/phoneValidation';
import { compact, merge, set } from 'lodash';
import { joiResolver } from '@hookform/resolvers/joi';
import useGetManagerProfile from '@/hooks/useGetManagerProfile';
import {
    AVATARS,
    BIG_ADMIN,
    BIG_AGENCY_MANAGER,
    BIG_AGENCY_OPERATOR,
    BIG_CLIENT_ADMIN,
    BIG_CLIENT_SITE_MANAGER,
    BIG_CLIENT_SITE_OPERATOR,
    BIG_MANAGER_TAG,
    BIG_OPERATOR,
    BIG_SUB_MANAGER_CLIENT_TAG,
    BIG_SUB_MANAGER_SITE_TAG,
    BIG_SUPER_ADMIN,
    MANAGER_PLACES,
    MANAGER_TAG,
    SUB_MANAGER_TAG,
    USER_PROFILES,
    USERS,
} from '@/utils/CONST';
import useGetCurrentUser from '@/hooks/useGetCurrentUser';
import { composeUri } from '@/utils/compose';

interface ProfileSelectProps {
    profiles: string[];
    onChange?: (value: string) => void;
}

const ProfileSelect = ({ profiles, onChange }: ProfileSelectProps) => {
    const translate = useTranslate();
    const { field, fieldState } = useInput({ source: 'tags' });

    const handleChange = (e: any) => {
        field.onChange(e);
        onChange?.(e.target.value);
    };

    const bigGroup = [BIG_SUPER_ADMIN, BIG_ADMIN, BIG_OPERATOR].filter((p) => profiles.includes(p));
    const clientGroup = [BIG_CLIENT_ADMIN, BIG_CLIENT_SITE_MANAGER, BIG_CLIENT_SITE_OPERATOR].filter((p) =>
        profiles.includes(p),
    );
    const agencyGroup = [BIG_AGENCY_MANAGER, BIG_AGENCY_OPERATOR].filter((p) => profiles.includes(p));

    return (
        <FormControl variant='outlined' fullWidth error={!!fieldState.error}>
            <InputLabel>{translate('users.list.user_type')}</InputLabel>
            <Select {...field} label={translate('users.list.user_type')} onChange={handleChange}>
                {bigGroup.length > 0 && (
                    <>
                        <ListSubheader>BIG</ListSubheader>
                        {bigGroup.map((p) => (
                            <MenuItem key={p} value={p}>
                                {translate(`users.profiles.${p.toLowerCase()}`)}
                            </MenuItem>
                        ))}
                    </>
                )}
                {clientGroup.length > 0 && (
                    <>
                        <ListSubheader>Client</ListSubheader>
                        {clientGroup.map((p) => (
                            <MenuItem key={p} value={p}>
                                {translate(`users.profiles.${p.toLowerCase()}`)}
                            </MenuItem>
                        ))}
                    </>
                )}
                {agencyGroup.length > 0 && (
                    <>
                        <ListSubheader>Agencie</ListSubheader>
                        {agencyGroup.map((p) => (
                            <MenuItem key={p} value={p}>
                                {translate(`users.profiles.${p.toLowerCase()}`)}
                            </MenuItem>
                        ))}
                    </>
                )}
            </Select>
        </FormControl>
    );
};

interface Props {
    select?: boolean;
    contextType?: 'client' | 'site';
}

const CreateUser = ({ select = false, contextType }: Props) => {
    const translate = useTranslate();
    const navigate = useNavigate();
    const { clientId, siteId } = useParams();
    const [create, { isLoading: isLoadingCreate }] = useCreate();
    const dataProvider = useDataProvider();
    const queryClient = useQueryClient();
    const { currentUser, isLoading: isLoadingUser } = useGetCurrentUser();
    const notify = useNotify();

    const navigateBack = () => {
        navigate(-1);
    };

    const isClientContext = contextType === 'client' || clientId !== undefined;
    const isSiteContext = contextType === 'site' || siteId !== undefined;
    const contextId = clientId ?? siteId;

    const profilesForCreate = USER_PROFILES;

    // Filtrar perfiles según el contexto
    const availableProfiles = isClientContext
        ? profilesForCreate.filter((profile) => profile === BIG_AGENCY_OPERATOR)
        : isSiteContext
          ? profilesForCreate.filter((profile) =>
                [BIG_CLIENT_SITE_MANAGER, BIG_CLIENT_SITE_OPERATOR].includes(profile),
            )
          : profilesForCreate;

    const [selectedProfile, setSelectedProfile] = useState<string>();
    const { profile, isLoading: isLoadingProfile } = useGetManagerProfile({
        profileName: selectedProfile,
    });

    const customJoi = Joi.extend(phoneExtension);
    const requiresTagSelection = select || isClientContext || isSiteContext;
    const schema = customJoi
        .object(
            merge(
                {},
                set({}, 'firstName', Joi.string().required()),
                set({}, 'lastName', Joi.string().required()),
                requiresTagSelection ? set({}, 'tags', Joi.string().required()) : {},
                set(
                    {},
                    'email',
                    Joi.string()
                        .email({ tlds: { allow: false } })
                        .required(),
                ),
            ),
        )
        .messages({
            'any.required': translate('ra.validation.required'),
            'string.base': translate('ra.validation.required'),
            'string.email': translate('ra.validation.email'),
        });

    const handleSubmit = (values: any) => {
        let user_tags = compact([
            ...([BIG_SUPER_ADMIN, BIG_ADMIN, BIG_OPERATOR].includes(values.tags)
                ? [MANAGER_TAG, BIG_MANAGER_TAG]
                : [SUB_MANAGER_TAG]),
            // Si es desde client, agregar BIG_SUB_MANAGER_CLIENT_TAG
            // Si es desde site, agregar BIG_SUB_MANAGER_SITE_TAG
            // Si no es desde context, usar la lógica original
            isClientContext
                ? BIG_SUB_MANAGER_CLIENT_TAG
                : isSiteContext
                  ? BIG_SUB_MANAGER_SITE_TAG
                  : [BIG_AGENCY_OPERATOR, BIG_AGENCY_MANAGER].includes(values.tags)
                    ? BIG_SUB_MANAGER_CLIENT_TAG
                    : [BIG_CLIENT_SITE_MANAGER, BIG_CLIENT_SITE_OPERATOR].includes(values.tags)
                      ? BIG_SUB_MANAGER_SITE_TAG
                      : undefined,
            values.tags,
        ]);
        delete values.tags;

        create(
            USERS,
            {
                data: {
                    ...values,
                    username: values['email'],
                    company: currentUser?.company,
                    tags: user_tags,
                    profiles: [profile?.id],
                },
                meta: {
                    realResource: `v2/${USERS}`,
                },
            },
            {
                onSuccess: async (createdUser: any) => {
                    // Si hay un contexto (client o site), vincular automáticamente
                    if (contextId && (isClientContext || isSiteContext)) {
                        try {
                            await dataProvider.create(MANAGER_PLACES, {
                                data: {
                                    placeOrEvent: composeUri(AVATARS, contextId),
                                    company: currentUser?.company,
                                    userManager: createdUser.id,
                                },
                            });
                            // Invalidar las queries de MANAGER_PLACES para refrescar la lista
                            queryClient.invalidateQueries({ queryKey: [MANAGER_PLACES] });
                            notify('ra.notification.created', { type: 'success' });
                            navigateBack();
                        } catch (error) {
                            // Aún así notificamos que el usuario fue creado
                            notify('ra.notification.created', { type: 'success' });
                            navigateBack();
                        }
                    } else {
                        notify('ra.notification.created', { type: 'success' });
                        navigateBack();
                    }
                },
                onError: ({ response }: any) => {
                    notify(`messages.${response.data['@type']}`);
                },
            },
        );
    };

    return (
        <DialogTemplate
            closeButtonDisabled={isLoadingCreate}
            title={translate('main.navigation.add_user')}
            icon={<PersonAddAltOutlined />}
            open={true}
        >
            <Form resolver={joiResolver(schema)} onSubmit={handleSubmit} reValidateMode={'onBlur'}>
                <Box
                    sx={{
                        display: 'grid',
                        gap: 2,
                    }}
                >
                    <FormInputsSeparator>
                        {[
                            { source: 'lastName', label: 'main.navigation.last_name' },
                            { source: 'firstName', label: 'main.navigation.name' },
                            { source: 'email', label: 'main.navigation.email' },
                        ].map(({ source, label }, index) => (
                            <TextInput key={index} helperText={false} name={source} source={source} label={label} />
                        ))}
                        {(select || isClientContext || isSiteContext) && (
                            <ProfileSelect
                                profiles={availableProfiles}
                                onChange={(value) => setSelectedProfile(value)}
                            />
                        )}
                    </FormInputsSeparator>

                    <ButtonsBlock>
                        <Button onClick={navigateBack} disabled={isLoadingCreate}>
                            {translate('ra.action.back')}
                        </Button>

                        <LoadingButton
                            loading={isLoadingCreate || isLoadingProfile || isLoadingUser}
                            type={'submit'}
                            variant={'contained'}
                        >
                            {translate('ra.action.create')}
                        </LoadingButton>
                    </ButtonsBlock>
                </Box>
            </Form>
        </DialogTemplate>
    );
};

export default CreateUser;
